/**
 * SiteFooter — persistent copyright + IP notice rendered on every page.
 *
 * Visible on marketing pages (public) AND proposal pages (post-NDA-gate).
 * Asserts Sterile Mate LLC's ownership of the methodology and dossiers
 * even before any visitor agrees to the click-wrap NDA.
 */
import { Link } from "react-router-dom";
import { ScrollText, Lock } from "lucide-react";

export default function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A192F] text-[#F9F7F1] border-t border-[#D4AF37]/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-8">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="font-display text-2xl mb-2">The <em className="italic">Bridge</em> Model</div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">
              A Sterile Mate LLC initiative · Houston, Texas
            </p>
            <p className="text-xs text-[#CBD5E1] leading-relaxed max-w-md">
              Municipal service-connector proposals for Harris County communities. Founder: George Broughton III, Sterile Mate LLC. SAM.gov-registered federal entity · CAGE Code 9GH66.
            </p>
          </div>

          {/* Nav */}
          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Site</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/" className="text-[#F9F7F1] hover:text-[#D4AF37] transition-colors">Home</Link></li>
              <li><Link to="/compare" className="text-[#F9F7F1] hover:text-[#D4AF37] transition-colors">Compare Proposals</Link></li>
              <li><Link to="/baytown" className="text-[#F9F7F1] hover:text-[#D4AF37] transition-colors">Proposal 01 · Baytown</Link></li>
              <li><Link to="/north-houston" className="text-[#F9F7F1] hover:text-[#D4AF37] transition-colors">Proposal 02 · North Houston District</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Schedule a Briefing</div>
            <p className="text-sm text-[#F9F7F1] mb-1">George Broughton III</p>
            <p className="text-xs text-[#CBD5E1] mb-3">Founder &amp; Program Architect · Sterile Mate LLC</p>
            <Link
              to="/#contact"
              data-testid="footer-contact-cta"
              className="inline-flex items-center gap-2 border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A192F] px-4 h-10 font-mono text-[10px] uppercase tracking-widest transition-colors"
            >
              Schedule a Briefing
            </Link>
          </div>
        </div>

        {/* IP / Copyright notice */}
        <div className="border-t border-[#F9F7F1]/15 pt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9 flex items-start gap-3">
            <ScrollText className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#94A3B8] uppercase tracking-widest font-mono leading-relaxed">
              © {year} Sterile Mate LLC · All rights reserved · The Bridge Model methodology — including the Hub-and-Spokes architecture, Service Connector caseload structure, three-portfolio framework, train-the-trainer curriculum, intake matrices, and the textual and graphic content of every proposal dossier — is the proprietary work product of George Broughton III, protected under U.S. copyright law, the Texas Uniform Trade Secrets Act, and the federal Defend Trade Secrets Act. Unauthorized reproduction, distribution, replication, or commercialization is strictly prohibited.
            </p>
          </div>
          <div className="lg:col-span-3 flex items-start gap-2">
            <Lock className="h-3.5 w-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#94A3B8] uppercase tracking-widest font-mono leading-relaxed">
              Proposal dossiers require a click-wrap reviewer acknowledgment before access. All acknowledgments are logged for audit. Document status: <span className="text-[#D4AF37]">Proposed · Pending Community Approval · Not Yet Adopted</span>.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
